import { useRef } from 'react'
import { notifications } from '@mantine/notifications'
import {
  useEvidenciaDelete,
  useEvidenciaList,
  useEvidenciaUpload,
} from '../../hooks/useEvidencias'

const MIME_PERMITIDOS = ['image/jpeg', 'image/png', 'image/heic', 'image/webp', 'application/pdf']
const TAMANHO_MAX_MB = 25

interface Props {
  ordemServicoId: string
  respostaItemId: string
  readonly?: boolean
}

function extractErrorMsg(err: unknown): string {
  const e = err as { response?: { data?: Record<string, unknown> } }
  const data = e?.response?.data ?? {}
  for (const k of ['arquivo', 'ordem_servico', 'detail']) {
    const v = data[k]
    if (Array.isArray(v) && v.length) return String(v[0])
    if (typeof v === 'string') return v
  }
  return 'Erro ao enviar foto.'
}

export default function RespostaItemEvidencias({
  ordemServicoId,
  respostaItemId,
  readonly,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null)
  const list = useEvidenciaList({ resposta_item: respostaItemId })
  const upload = useEvidenciaUpload()
  const del = useEvidenciaDelete()

  async function handleUpload(file: File) {
    if (!MIME_PERMITIDOS.includes(file.type)) {
      notifications.show({
        color: 'red',
        message: `Tipo "${file.type}" não permitido. Aceitos: JPEG, PNG, HEIC, WebP, PDF.`,
      })
      return
    }
    if (file.size > TAMANHO_MAX_MB * 1024 * 1024) {
      notifications.show({
        color: 'red',
        message: `Arquivo excede ${TAMANHO_MAX_MB}MB.`,
      })
      return
    }
    try {
      await upload.mutateAsync({
        ordemServicoId,
        arquivo: file,
        tipo: 'foto',
        respostaItemId,
      })
      notifications.show({ color: 'green', message: 'Foto anexada.' })
      if (inputRef.current) inputRef.current.value = ''
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Falha ao anexar foto',
        message: extractErrorMsg(err),
      })
    }
  }

  function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (file) void handleUpload(file)
  }

  async function handleDelete(id: string) {
    if (!confirm('Remover foto?')) return
    try {
      await del.mutateAsync(id)
      notifications.show({ color: 'green', message: 'Foto removida.' })
    } catch (err) {
      notifications.show({
        color: 'red',
        message: extractErrorMsg(err),
      })
    }
  }

  const items = list.data?.results ?? []

  return (
    <div
      data-testid={`evidencias-item-${respostaItemId}`}
      style={{ marginTop: '0.5rem' }}
    >
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
        {!readonly && (
          <>
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              data-testid={`btn-anexar-foto-${respostaItemId}`}
              disabled={upload.isPending}
              style={{
                padding: '0.3rem 0.7rem',
                fontSize: '0.85rem',
                background: '#FFF3E0',
                border: '1px dashed #E65100',
                color: '#E65100',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              {upload.isPending ? 'Enviando...' : '📷 Anexar foto'}
            </button>
            <input
              ref={inputRef}
              type="file"
              accept={MIME_PERMITIDOS.join(',')}
              onChange={onFileChange}
              style={{ display: 'none' }}
            />
          </>
        )}

        {items.map((ev) => (
          <div
            key={ev.id}
            data-testid={`evid-item-${ev.id}`}
            style={{
              display: 'inline-flex',
              gap: '0.25rem',
              alignItems: 'center',
              border: '1px solid #E0E0E0',
              borderRadius: '4px',
              padding: '0.15rem 0.3rem',
              fontSize: '0.75rem',
              background: 'white',
            }}
          >
            {ev.mime_type.startsWith('image/') && ev.arquivo_url ? (
              <a href={ev.arquivo_url} target="_blank" rel="noreferrer">
                <img
                  src={ev.arquivo_url}
                  alt=""
                  style={{ width: 28, height: 28, objectFit: 'cover', borderRadius: '2px' }}
                />
              </a>
            ) : (
              <span>📄</span>
            )}
            <span title={ev.descricao}>{(ev.tamanho_bytes / 1024).toFixed(0)} KB</span>
            {!readonly && (
              <button
                type="button"
                onClick={() => handleDelete(ev.id)}
                title="Remover"
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#C62828',
                  cursor: 'pointer',
                  padding: 0,
                  fontSize: '0.85rem',
                }}
              >
                ×
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
